import React from 'react';
import { createUseStyles } from 'react-jss';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// Single source of truth for what the help modal documents.
// Keep in sync with the actual handlers in Game.js's window keydown listener.
const SHORTCUT_GROUPS = [
  {
    titleKey: 'shortcuts.movement',
    items: [
      { keys: ['↑', '↓', '←', '→'], labelKey: 'shortcuts.slide' },
      { keys: ['Swipe'], labelKey: 'shortcuts.slideTouch' },
    ],
  },
  {
    titleKey: 'shortcuts.selection',
    items: [
      { keys: ['Click / tap'], labelKey: 'shortcuts.select' },
      { keys: ['Esc'], labelKey: 'shortcuts.deselect' },
    ],
  },
  {
    titleKey: 'shortcuts.bidding',
    items: [
      { keys: ['0–9'], labelKey: 'shortcuts.startBid' },
      { keys: ['Enter'], labelKey: 'shortcuts.placeBid' },
      { keys: ['Esc'], labelKey: 'shortcuts.cancelBid' },
    ],
  },
  {
    titleKey: 'shortcuts.general',
    items: [
      { keys: ['Ctrl / ⌘', 'Z'], labelKey: 'shortcuts.undo' },
      { keys: ['?'], labelKey: 'shortcuts.toggleHelp' },
      { keys: ['Esc'], labelKey: 'shortcuts.closePanel' },
    ],
  },
];

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,6,3,0.68)',
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  modal: {
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '24px',
    maxWidth: '340px',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    maxHeight: '80vh',
    overflowY: 'auto',
    ...scrollbar,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 20px 50px rgba(0,0,0,0.55)',
    ].join(','),
  },
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '40px',
    height: '40px',
    background: 'none',
    border: 'none',
    color: '#9c8168',
    fontSize: '16px',
    cursor: 'pointer',
    lineHeight: 1,
    fontFamily: 'inherit',
    borderRadius: '8px',
    '&:active': {
      background: 'rgba(255,255,255,0.06)',
    },
  },
  heading: {
    margin: 0,
    color: '#f5e6c8',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionTitle: {
    fontFamily: MONO,
    color: '#c4a070',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  keys: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },
  kbd: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    color: '#e8d6ae',
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '5px',
    padding: '2px 8px',
    lineHeight: 1.4,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
  },
  label: {
    color: '#d9c3a2',
    fontSize: '13px',
    textAlign: 'right',
  },
});

function ShortcutsModal({ open, onClose }) {
  const classes = useStyles();
  const t = useT();

  useModalEscape(open, onClose);

  if (!open) return null;

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div
        className={classes.modal}
        role="dialog"
        aria-modal="true"
        aria-label={t('shortcuts.title')}
        onClick={e => e.stopPropagation()}
      >
        <button className={classes.closeBtn} onClick={onClose} aria-label={t('a11y.close')}>✕</button>

        <h3 className={classes.heading}>{t('shortcuts.title')}</h3>

        {SHORTCUT_GROUPS.map(group => (
          <div key={group.titleKey} className={classes.group}>
            <div className={classes.sectionTitle}>{t(group.titleKey)}</div>
            {group.items.map(item => (
              <div key={item.labelKey} className={classes.row}>
                <span className={classes.keys}>
                  {item.keys.map(k => (
                    <kbd key={k} className={classes.kbd}>{k}</kbd>
                  ))}
                </span>
                <span className={classes.label}>{t(item.labelKey)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ShortcutsModal;
